import React from 'react';
import { CheckCircle2, ArrowRight, TrendingDown, Wallet, Sparkles, Award, Star, ShieldCheck } from 'lucide-react';
import { Button } from '../ui/button';
import { computeConsolidation, formatCurrency } from '../../lib/proposal/calc';

/* Slide deck for `consolidation` proposal type.
 *
 * Pure presentational components — every slide receives:
 *   - payload  (proposal payload, may be partial)
 *   - calc     (computed result of computeConsolidation)
 *   - clientName, brokerName
 *   - readonly (true for client / revisit; false for broker)
 *   - onAccept (called when client clicks Accept on slide 5)
 *   - accepted (already-accepted proposals show green confirmation)
 */

const Container = ({ children, dark = false }) => (
  <div
    className={`relative w-full min-h-full flex flex-col px-5 sm:px-10 md:px-16 overflow-x-hidden ${
      dark
        ? 'bg-gradient-to-br from-slate-950 via-slate-900 to-emerald-950 text-white'
        : 'bg-gradient-to-br from-slate-50 via-white to-emerald-50 text-slate-900'
    }`}
  >
    {/* Depth — radial brand glow + subtle grain overlay */}
    <div
      aria-hidden="true"
      className={`pointer-events-none absolute inset-0 ${dark ? 'opacity-50' : 'opacity-30'}`}
      style={{
        backgroundImage: dark
          ? 'radial-gradient(ellipse at top right, rgba(16,185,129,0.18), transparent 60%), radial-gradient(ellipse at bottom left, rgba(59,130,246,0.10), transparent 50%)'
          : 'radial-gradient(ellipse at top right, rgba(16,185,129,0.12), transparent 50%)',
      }}
    />
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 mix-blend-overlay opacity-[0.04]"
      style={{
        backgroundImage:
          "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/%3E%3CfeColorMatrix values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 1 0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
      }}
    />
    {/* py-8 + my-auto on the inner block lets the content vertically center
        ONLY when it's shorter than the slide; tall slides scroll naturally
        without clipping the top/bottom. */}
    <div className="relative w-full max-w-6xl mx-auto py-8 my-auto">
      {children}
    </div>
  </div>
);

const Eyebrow = ({ children, dark = true }) => (
  <p className={`text-xs uppercase tracking-[0.2em] font-bold mb-3 ${dark ? 'text-emerald-400' : 'text-emerald-600'}`}>{children}</p>
);

// ─────────────────── Slide 1: Cover ───────────────────
const SlideCover = ({ payload, clientName, brokerName }) => (
  <Container dark>
    <Eyebrow>Live Consultation</Eyebrow>
    <h1 className="text-4xl md:text-6xl font-bold leading-tight">
      A clearer path to <span className="text-emerald-400">debt-free</span>.
    </h1>
    {clientName && (
      <p className="mt-6 text-lg md:text-xl text-slate-300">
        Prepared for <span className="text-white font-semibold">{clientName}</span>{brokerName ? <> by <span className="text-white font-semibold">{brokerName}</span></> : null}.
      </p>
    )}
    <p className="mt-2 text-sm text-slate-400 max-w-xl">
      A line-by-line breakdown of how rolling existing debts into your home loan unlocks a real, measurable interest reduction — and gets you mortgage-free faster.
    </p>
    <div className="mt-10 flex items-center gap-2 text-xs text-slate-500">
      <Sparkles className="h-4 w-4 text-emerald-400" />
      Live, interactive — your numbers, your strategy.
    </div>
  </Container>
);

// ─────────────────── Slide 2: Current Situation ───────────────────
const Stat = ({ label, value, sub, accent = false }) => (
  <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-4">
    <p className="text-[11px] uppercase tracking-wide font-bold text-slate-500">{label}</p>
    <p className={`text-2xl font-bold mt-1 ${accent ? 'text-rose-600' : 'text-slate-900'}`}>{value}</p>
    {sub && <p className="text-[11px] text-slate-500 mt-1">{sub}</p>}
  </div>
);

const SlideCurrent = ({ payload, calc }) => {
  const debts = Array.isArray(payload?.debts) ? payload.debts : [];
  const mortgage = payload?.mortgage || {};
  return (
    <Container>
      <Eyebrow dark={false}>Where you're at today</Eyebrow>
      <h2 className="text-3xl md:text-5xl font-bold mb-6">Your current position</h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Stat label="Total debts" value={formatCurrency(calc.current.totalDebtAmount)} sub={`${debts.length} accounts`} />
        <Stat label="Avg debt rate" value={`${calc.current.averageRate.toFixed(2)}%`} sub="Weighted" accent />
        <Stat label="Mortgage balance" value={formatCurrency(mortgage.remainingBalance)} sub={`${mortgage.interestRate || 0}%`} />
        <Stat label="Monthly outlay" value={formatCurrency(calc.current.totalMonthlyPayment)} sub="Across all debts + mortgage" />
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="bg-slate-900 text-white px-4 py-2 text-sm font-semibold uppercase tracking-wide">Debts</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                <th className="text-left py-2 px-3 font-semibold text-slate-600">Debt</th>
                <th className="text-right py-2 px-3 font-semibold text-slate-600">Balance</th>
                <th className="text-right py-2 px-3 font-semibold text-slate-600">Rate</th>
                <th className="text-right py-2 px-3 font-semibold text-slate-600">Monthly</th>
              </tr>
            </thead>
            <tbody>
              {debts.length === 0 ? (
                <tr><td colSpan={4} className="py-4 px-3 text-center text-slate-400 italic">No debts entered yet.</td></tr>
              ) : debts.map((d, i) => (
                <tr key={i} className="border-b border-slate-100">
                  <td className="py-2 px-3 font-medium">{d.name || `Debt ${i + 1}`}</td>
                  <td className="py-2 px-3 text-right">{formatCurrency(d.amount)}</td>
                  <td className="py-2 px-3 text-right text-rose-600 font-medium">{Number(d.rate || 0).toFixed(2)}%</td>
                  <td className="py-2 px-3 text-right">{formatCurrency(((d.amount || 0) * (d.rate || 0) / 100 / 12) + 0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Container>
  );
};

// ─────────────────── Slide 3: The Strategy ───────────────────
const StepCard = ({ n, title, body, accent }) => (
  <div className={`flex-1 rounded-xl bg-white shadow-sm border border-slate-200 p-5 relative overflow-hidden`}>
    <div className={`absolute top-0 left-0 w-1.5 h-full ${accent}`} />
    <p className="text-7xl font-bold absolute -right-2 top-1 text-slate-100 select-none leading-none">{n}</p>
    <p className={`text-xs font-bold uppercase tracking-wide mb-2 relative ${accent === 'bg-emerald-500' ? 'text-emerald-600' : accent === 'bg-blue-500' ? 'text-blue-600' : 'text-violet-600'}`}>STEP {n}</p>
    <h3 className="text-lg font-bold mb-2 relative">{title}</h3>
    <p className="text-sm text-slate-600 relative leading-relaxed">{body}</p>
  </div>
);

const SlideStrategy = ({ payload, calc }) => {
  const consolidationRate = Number(payload?.consolidationRate || 0);
  return (
    <Container>
      <Eyebrow dark={false}>The plan</Eyebrow>
      <h2 className="text-3xl md:text-5xl font-bold mb-2">Three steps. One outcome.</h2>
      <p className="text-slate-600 mb-6">Replace fragmented high-interest debts with a single, lower mortgage rate — then accelerate the payoff.</p>
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <StepCard
          n="01"
          title="Refinance"
          body={`Roll ${formatCurrency(calc.current.totalDebtAmount)} of high-interest debt into your home loan, taking the new balance to ${formatCurrency(calc.consolidated.newMortgageAmount)}.`}
          accent="bg-emerald-500"
        />
        <StepCard
          n="02"
          title="Single Rate"
          body={`Replace a weighted average rate of ${calc.current.averageRate.toFixed(2)}% with a single mortgage rate of ${consolidationRate.toFixed(2)}%.`}
          accent="bg-blue-500"
        />
        <StepCard
          n="03"
          title="Redraw"
          body={`Park the ${formatCurrency(calc.consolidated.monthlySavings)}/mo reduction into the redraw account so the balance falls faster.`}
          accent="bg-violet-500"
        />
      </div>

      {/* How redraw works narrative — mirrors Results.js explainer */}
      <div className="bg-gradient-to-br from-slate-50 to-emerald-50 border border-emerald-200 rounded-2xl p-5">
        <p className="text-[11px] uppercase tracking-wide font-bold text-emerald-700 mb-2">How the Redraw Strategy Works</p>
        <div className="space-y-2 text-sm text-slate-700 leading-relaxed">
          <p>
            By consolidating your high-interest debts into your mortgage, you immediately reduce your monthly payment by <strong className="text-emerald-700">{formatCurrency(calc.consolidated.monthlySavings)}</strong>.
          </p>
          <p>
            Instead of spending that reduction, deposit it into a redraw account linked to your mortgage. Your <em>actual</em> outgoings stay at <strong>{formatCurrency(calc.current.totalMonthlyPayment)}/mo</strong> — but every extra dollar now attacks the mortgage at the lower rate.
          </p>
          <p>
            The result: mortgage paid off <strong className="text-emerald-700">{calc.redrawStrategy.yearsSaved} years earlier</strong>, with a total interest reduction of <strong className="text-emerald-700">{formatCurrency(calc.redrawStrategy.totalSavings)}</strong>.
          </p>
        </div>
      </div>
    </Container>
  );
};

// ─────────────────── NEW Slide: What We're Proposing ───────────────────
// Two clearly numbered sections:
//  1. The Proposal — debt-by-debt table → Combined → New Mortgage merge → 3 steps
//  2. What You'll Reduce — headline wins (3 quick stats)
//
// Mobile-aware: the debt table renders as cards at <sm and as a table at >=sm
// so smartphone clients still see all the columns of data without horizontal
// overflow.
const monthlyAmort = (principal, annualRatePct, years) => {
  const P = Number(principal) || 0;
  const r = (Number(annualRatePct) || 0) / 100 / 12;
  const n = (Number(years) || 30) * 12;
  if (!P || !n) return 0;
  if (r === 0) return P / n;
  return (P * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
};

const StepMini = ({ n, title, body, accent, accentText }) => (
  <div className="rounded-xl bg-white border border-slate-200 p-3 sm:p-4 shadow-sm">
    <div className={`inline-flex items-center justify-center h-7 w-7 rounded-full ${accent} text-white text-xs font-bold mb-1.5`}>
      {n}
    </div>
    <p className={`text-[11px] uppercase tracking-wide font-bold mb-1 ${accentText}`}>{title}</p>
    <p className="text-xs sm:text-sm text-slate-700 leading-relaxed">{body}</p>
  </div>
);

const SlideProposal = ({ payload, calc }) => {
  const consolidationRate = Number(payload?.consolidationRate || 0);
  const mortgage = payload?.mortgage || {};
  const debts = Array.isArray(payload?.debts) ? payload.debts.filter((d) => Number(d?.amount) > 0) : [];
  const monthlyReductionPct = calc.current.totalMonthlyPayment
    ? ((calc.consolidated.monthlySavings / calc.current.totalMonthlyPayment) * 100)
    : 0;

  // Build a unified row list: the existing mortgage + each debt.
  const rows = [
    {
      name: 'Existing Mortgage',
      balance: Number(mortgage.remainingBalance) || 0,
      currentRate: Number(mortgage.interestRate) || 0,
      currentMonthly: monthlyAmort(mortgage.remainingBalance, mortgage.interestRate, mortgage.remainingTerm || 30),
    },
    ...debts.map((d) => ({
      name: d.name || 'Debt',
      balance: Number(d.amount) || 0,
      currentRate: Number(d.rate) || 0,
      currentMonthly: monthlyAmort(d.amount, d.rate, d.term || 5),
    })),
  ];

  return (
    <Container>
      <Eyebrow dark={false}>What We're Proposing</Eyebrow>
      <h2 className="text-3xl md:text-5xl font-bold mb-2">Here's the plan, in two parts.</h2>
      <p className="text-slate-600 mb-6 text-sm max-w-2xl">A clear, broker-recommended structure designed around your numbers — not a generic template.</p>

      {/* ── 1. The Proposal ── */}
      <div className="bg-white rounded-2xl border-2 border-blue-300 shadow-sm p-4 sm:p-5 mb-4">
        <div className="flex items-baseline gap-3 mb-3">
          <span className="text-3xl font-bold text-blue-600">1.</span>
          <h3 className="text-xl font-bold text-slate-900">The Proposal</h3>
        </div>

        {/* Mobile: stacked cards. Desktop (≥sm): proper table. */}
        <div className="sm:hidden space-y-2">
          {rows.map((r, i) => (
            <div key={i} className="border border-slate-200 rounded-lg p-3 bg-slate-50">
              <p className="text-sm font-bold text-slate-900 mb-1">{r.name}</p>
              <div className="grid grid-cols-2 gap-y-1 text-xs">
                <span className="text-slate-500">Balance</span>
                <span className="text-right font-semibold">{formatCurrency(r.balance)}</span>
                <span className="text-slate-500">Current rate</span>
                <span className="text-right text-rose-600 font-semibold">{r.currentRate.toFixed(2)}%</span>
                <span className="text-slate-500">Current monthly</span>
                <span className="text-right">{formatCurrency(r.currentMonthly)}</span>
                <span className="text-slate-500">Proposed rate</span>
                <span className="text-right text-emerald-700 font-bold">{consolidationRate.toFixed(2)}%</span>
              </div>
            </div>
          ))}
        </div>
        <div className="hidden sm:block overflow-x-auto rounded-lg border border-slate-200">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left py-2 px-3 text-[10px] font-bold uppercase tracking-wide">Debt</th>
                <th className="text-right py-2 px-3 text-[10px] font-bold uppercase tracking-wide">Balance</th>
                <th className="text-right py-2 px-3 text-[10px] font-bold uppercase tracking-wide">Current Rate</th>
                <th className="text-right py-2 px-3 text-[10px] font-bold uppercase tracking-wide">Current Monthly</th>
                <th className="text-right py-2 px-3 text-[10px] font-bold uppercase tracking-wide bg-emerald-100 text-emerald-700">Proposed Rate</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-t border-slate-100">
                  <td className="py-2 px-3 font-medium text-slate-900">{r.name}</td>
                  <td className="py-2 px-3 text-right">{formatCurrency(r.balance)}</td>
                  <td className="py-2 px-3 text-right text-rose-600 font-medium">{r.currentRate.toFixed(2)}%</td>
                  <td className="py-2 px-3 text-right">{formatCurrency(r.currentMonthly)}</td>
                  <td className="py-2 px-3 text-right bg-emerald-50/40 text-emerald-700 font-bold">{consolidationRate.toFixed(2)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Combined → New Mortgage merge row */}
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] gap-3 sm:gap-2 items-center">
          <div className="rounded-xl bg-slate-900 text-white p-3 sm:p-4 text-center">
            <p className="text-[10px] uppercase tracking-wide font-bold text-slate-400 mb-1">Combined today</p>
            <p className="text-2xl font-bold">{formatCurrency(calc.consolidated.newMortgageAmount)}</p>
            <p className="text-[10px] text-slate-400 mt-1">Blended rate <span className="text-rose-400 font-semibold">{calc.current.averageRate.toFixed(2)}%</span> · {formatCurrency(calc.current.totalMonthlyPayment)}/mo</p>
          </div>
          <div className="flex justify-center text-emerald-500 text-2xl font-bold">→</div>
          <div className="rounded-xl bg-emerald-600 text-white p-3 sm:p-4 text-center proposal-pulse-glow">
            <p className="text-[10px] uppercase tracking-wide font-bold text-emerald-200 mb-1">New Mortgage</p>
            <p className="text-2xl font-bold">{formatCurrency(calc.consolidated.newMortgageAmount)}</p>
            <p className="text-[10px] text-emerald-100 mt-1">Single rate <span className="font-bold">{consolidationRate.toFixed(2)}%</span> · {formatCurrency(calc.consolidated.monthlyPayment)}/mo</p>
          </div>
        </div>

        {/* 3 mini step cards under the merge */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
          <StepMini n="1" title="Refinance & Consolidate" accent="bg-emerald-500" accentText="text-emerald-700"
            body={`Roll ${formatCurrency(calc.current.totalDebtAmount)} of high-interest debt into the home loan.`} />
          <StepMini n="2" title="Single Lower Rate" accent="bg-blue-500" accentText="text-blue-700"
            body={`Replace a ${calc.current.averageRate.toFixed(2)}% blended rate with one mortgage at ${consolidationRate.toFixed(2)}%.`} />
          <StepMini n="3" title="Redraw Strategy" accent="bg-violet-500" accentText="text-violet-700"
            body={`Park the ${formatCurrency(calc.consolidated.monthlySavings)}/mo cash freed up into redraw — same outgoings, faster payoff.`} />
        </div>
      </div>

      {/* ── 2. What You'll Reduce ── */}
      <div className="bg-white rounded-2xl border-2 border-emerald-300 shadow-sm p-4 sm:p-5">
        <div className="flex items-baseline gap-3 mb-3">
          <span className="text-3xl font-bold text-emerald-600">2.</span>
          <h3 className="text-xl font-bold text-slate-900">What You'll Reduce</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
            <p className="text-[10px] uppercase tracking-wide font-bold text-emerald-700">Monthly outlay</p>
            <p className="text-2xl font-bold text-slate-900 mt-1 proposal-pulse-number">{formatCurrency(calc.consolidated.monthlySavings)}</p>
            <p className="text-[10px] text-slate-500 mt-0.5">freed up every month ({monthlyReductionPct.toFixed(1)}% lower)</p>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-[10px] uppercase tracking-wide font-bold text-blue-700">Lifetime interest</p>
            <p className="text-2xl font-bold text-slate-900 mt-1 proposal-pulse-number">{formatCurrency(calc.redrawStrategy.totalSavings)}</p>
            <p className="text-[10px] text-slate-500 mt-0.5">total reduction with redraw applied</p>
          </div>
          <div className="bg-violet-50 border border-violet-200 rounded-lg p-3">
            <p className="text-[10px] uppercase tracking-wide font-bold text-violet-700">Time to mortgage-free</p>
            <p className="text-2xl font-bold text-slate-900 mt-1 proposal-pulse-number">{calc.redrawStrategy.yearsSaved} yrs</p>
            <p className="text-[10px] text-slate-500 mt-0.5">earlier than your current schedule</p>
          </div>
        </div>
      </div>
    </Container>
  );
};

// ─────────────────── NEW Slide: Debt Consolidation Reduction ───────────────────
// Mirrors the Results.js "Debt Consolidation Reduction" card 1:1 — focuses on
// the IMMEDIATE consolidation-only win (no redraw effect yet) so the client
// sees the value of step 1 before we layer step 3 on top.
const SlideConsolidationReduction = ({ payload, calc }) => {
  const consolidationRate = Number(payload?.consolidationRate || 0);
  const monthlyReductionPct = calc.current.totalMonthlyPayment
    ? ((calc.consolidated.monthlySavings / calc.current.totalMonthlyPayment) * 100)
    : 0;
  return (
    <Container>
      <Eyebrow dark={false}>Step 1 of 3</Eyebrow>
      <h2 className="text-3xl md:text-5xl font-bold mb-2">Debt Consolidation Reduction</h2>
      <p className="text-slate-600 mb-6 text-sm max-w-2xl">The immediate win from rolling your debts into the mortgage — <em>before</em> we even apply the redraw strategy.</p>

      {/* Top stats — exact mirror of the Results.js layout */}
      <div className="grid md:grid-cols-3 gap-4 mb-5">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
          <p className="text-xs text-slate-500">Total Debt Amount</p>
          <p className="text-2xl font-bold text-slate-900 mt-1">{formatCurrency(calc.current.totalDebtAmount)}</p>
          <p className="text-[10px] text-slate-500 mt-1">Being consolidated</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
          <p className="text-xs text-slate-500">Average Interest Rate</p>
          <p className="text-2xl font-bold text-rose-600 mt-1">{calc.current.averageRate.toFixed(2)}%</p>
          <p className="text-[10px] text-slate-500 mt-1">Current weighted average</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
          <p className="text-xs text-slate-500">New Consolidation Rate</p>
          <p className="text-2xl font-bold text-emerald-600 mt-1">{consolidationRate.toFixed(2)}%</p>
          <p className="text-[10px] text-slate-500 mt-1">Single mortgage rate</p>
        </div>
      </div>

      {/* Two big-callout reduction cards */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-emerald-50 border-2 border-emerald-200 rounded-xl p-4">
          <div className="flex items-center justify-between mb-1">
            <p className="text-sm font-semibold text-slate-700">Monthly Payment Reduction</p>
            <CheckCircle2 className="h-5 w-5 text-emerald-600" />
          </div>
          <p className="text-3xl font-bold text-emerald-700 mb-1">{formatCurrency(calc.consolidated.monthlySavings)}</p>
          <p className="text-xs text-slate-600">From {formatCurrency(calc.current.totalMonthlyPayment)} to {formatCurrency(calc.consolidated.monthlyPayment)}</p>
          <p className="text-[11px] text-emerald-600 font-medium mt-1.5">Reduce {monthlyReductionPct.toFixed(1)}% monthly</p>
        </div>
        <div className="bg-emerald-50 border-2 border-emerald-200 rounded-xl p-4">
          <div className="flex items-center justify-between mb-1">
            <p className="text-sm font-semibold text-slate-700">Total Interest Reduction</p>
            <TrendingDown className="h-5 w-5 text-emerald-600" />
          </div>
          <p className="text-3xl font-bold text-emerald-700 mb-1">{formatCurrency(calc.consolidated.totalInterestSavings)}</p>
          <p className="text-xs text-slate-600">Just from consolidation alone</p>
          <p className="text-[11px] text-emerald-600 font-medium mt-1.5">Before even using redraw strategy</p>
        </div>
      </div>
    </Container>
  );
};

// ─────────────────── Slide: Detailed Comparison (was "Your Reduction") ───────────────────
const HeroTile = ({ label, value, sub, accentRing, pulse = false }) => (
  <div className={`relative bg-white rounded-2xl border-2 ${accentRing} p-5 shadow-sm ${pulse ? 'proposal-pulse-glow' : ''}`}>
    {pulse && <div className="absolute inset-0 rounded-2xl proposal-shimmer pointer-events-none" />}
    <p className="text-[11px] uppercase tracking-wide font-bold text-slate-500 relative">{label}</p>
    <p className={`text-3xl md:text-4xl font-bold text-slate-900 mt-2 relative ${pulse ? 'proposal-pulse-number' : ''}`}>{value}</p>
    {sub && <p className="text-xs text-slate-500 mt-1 relative">{sub}</p>}
  </div>
);

const SlideReduction = ({ payload, calc }) => {
  const consolidationRate = Number(payload?.consolidationRate || 0);
  const remainingMonths = (payload?.mortgage?.remainingTerm || 0) * 12;
  const monthlyReductionPct = calc.current.totalMonthlyPayment
    ? ((calc.consolidated.monthlySavings / calc.current.totalMonthlyPayment) * 100)
    : 0;
  const monthsSaved = calc.redrawStrategy.monthsSaved || 0;
  const formatMonths = (m) => {
    const yrs = Math.floor(m / 12);
    const rem = Math.round(m % 12);
    if (yrs === 0) return `${rem} mo`;
    if (rem === 0) return `${yrs} yr${yrs === 1 ? '' : 's'}`;
    return `${yrs} yr${yrs === 1 ? '' : 's'} ${rem} mo`;
  };

  return (
    <Container dark>
      <Eyebrow>Detailed Comparison</Eyebrow>
      <h2 className="text-3xl md:text-5xl font-bold mb-2">Your reduction</h2>
      <p className="text-slate-300 text-sm mb-6 max-w-2xl">A line-by-line breakdown of the cash you free up monthly, lifetime interest reduction, and how much sooner you become mortgage-free.</p>

      {/* Headline tiles — pulse to draw the eye */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <HeroTile
          label="Cash freed up monthly"
          value={formatCurrency(calc.consolidated.monthlySavings)}
          sub={`From ${formatCurrency(calc.current.totalMonthlyPayment)} → ${formatCurrency(calc.consolidated.monthlyPayment)} (${monthlyReductionPct.toFixed(1)}% lower)`}
          accentRing="border-emerald-400"
          pulse
        />
        <HeroTile
          label="Total interest reduction"
          value={formatCurrency(calc.redrawStrategy.totalSavings)}
          sub={`vs current setup, with redraw applied`}
          accentRing="border-blue-400"
          pulse
        />
        <HeroTile
          label="Mortgage-free"
          value={`${calc.redrawStrategy.yearsSaved} yrs early`}
          sub={`${formatMonths(monthsSaved)} sooner than current schedule`}
          accentRing="border-violet-400"
          pulse
        />
      </div>

      {/* Min repayment options — same data the broker page surfaces */}
      <div className="bg-emerald-500/10 border border-emerald-400/30 rounded-xl p-4 mb-6">
        <p className="text-[11px] uppercase tracking-wide font-bold text-emerald-300 mb-3">Minimum total repayments</p>
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white/95 text-slate-900 rounded-lg p-3 text-center">
            <p className="text-[10px] uppercase tracking-wide font-bold text-slate-500">Weekly</p>
            <p className="text-lg md:text-xl font-bold text-emerald-700 mt-1">{formatCurrency(calc.minRepayments.weekly)}</p>
            <p className="text-[10px] text-slate-500">per week</p>
          </div>
          <div className="bg-white/95 text-slate-900 rounded-lg p-3 text-center">
            <p className="text-[10px] uppercase tracking-wide font-bold text-slate-500">Fortnightly</p>
            <p className="text-lg md:text-xl font-bold text-emerald-700 mt-1">{formatCurrency(calc.minRepayments.fortnightly)}</p>
            <p className="text-[10px] text-slate-500">every 2 weeks</p>
          </div>
          <div className="bg-white/95 text-slate-900 rounded-lg p-3 text-center">
            <p className="text-[10px] uppercase tracking-wide font-bold text-slate-500">Monthly</p>
            <p className="text-lg md:text-xl font-bold text-emerald-700 mt-1">{formatCurrency(calc.minRepayments.monthly)}</p>
            <p className="text-[10px] text-slate-500">per month</p>
          </div>
        </div>
        <p className="text-[10px] text-emerald-200/70 mt-2">Based on {formatCurrency(calc.consolidated.newMortgageAmount)} at {consolidationRate.toFixed(2)}% over 360 months.</p>
      </div>

      {/* Comprehensive 3-column comparison — Current / Consolidated / + Redraw */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden text-slate-900">
        <div className="bg-slate-900 text-white px-4 py-2 text-sm font-semibold uppercase tracking-wide flex items-center gap-2">
          <TrendingDown className="h-4 w-4 text-emerald-400" />
          Side-by-side comparison
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-slate-50">
                <th className="text-left py-2.5 px-3 font-semibold text-slate-600">Metric</th>
                <th className="text-right py-2.5 px-3 font-semibold text-slate-600">Current</th>
                <th className="text-right py-2.5 px-3 font-semibold text-blue-700 bg-blue-50/50">Refinanced</th>
                <th className="text-right py-2.5 px-3 font-semibold text-emerald-700 bg-emerald-50/50">Refinanced + Redraw</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b">
                <td className="py-2.5 px-3 font-medium">Monthly outlay</td>
                <td className="py-2.5 px-3 text-right">{formatCurrency(calc.current.totalMonthlyPayment)}</td>
                <td className="py-2.5 px-3 text-right text-blue-700 bg-blue-50/50">
                  {formatCurrency(calc.consolidated.monthlyPayment)}
                  <div className="text-[10px] text-emerald-600 font-medium">−{formatCurrency(calc.consolidated.monthlySavings)}/mo</div>
                </td>
                <td className="py-2.5 px-3 text-right text-emerald-700 font-bold bg-emerald-50/50">
                  {formatCurrency(calc.redrawStrategy.monthlyPayment)}
                  <div className="text-[10px] text-slate-500 font-normal">same total spend</div>
                </td>
              </tr>
              <tr className="border-b">
                <td className="py-2.5 px-3 font-medium">Average rate</td>
                <td className="py-2.5 px-3 text-right text-rose-600 font-medium">{calc.current.averageRate.toFixed(2)}%</td>
                <td className="py-2.5 px-3 text-right text-emerald-600 bg-blue-50/50">{consolidationRate.toFixed(2)}%</td>
                <td className="py-2.5 px-3 text-right text-emerald-600 bg-emerald-50/50">{consolidationRate.toFixed(2)}%</td>
              </tr>
              <tr className="border-b">
                <td className="py-2.5 px-3 font-medium">Total interest paid</td>
                <td className="py-2.5 px-3 text-right">{formatCurrency(calc.current.totalInterest)}</td>
                <td className="py-2.5 px-3 text-right text-blue-700 bg-blue-50/50">
                  {formatCurrency(calc.consolidated.totalInterest)}
                  <div className="text-[10px] text-emerald-600 font-medium">−{formatCurrency(calc.consolidated.totalInterestSavings)}</div>
                </td>
                <td className="py-2.5 px-3 text-right text-emerald-700 font-bold bg-emerald-50/50">
                  {formatCurrency(calc.redrawStrategy.totalInterest)}
                  <div className="text-[10px] text-emerald-600 font-medium">−{formatCurrency(calc.redrawStrategy.additionalSavings)} more</div>
                </td>
              </tr>
              <tr>
                <td className="py-2.5 px-3 font-medium">Mortgage payoff</td>
                <td className="py-2.5 px-3 text-right">—</td>
                <td className="py-2.5 px-3 text-right bg-blue-50/50">{Math.floor(remainingMonths / 12)} yrs</td>
                <td className="py-2.5 px-3 text-right text-emerald-700 font-bold bg-emerald-50/50">
                  {formatMonths(remainingMonths - monthsSaved)}
                  <div className="text-[10px] text-emerald-600 font-medium">{formatMonths(monthsSaved)} sooner</div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </Container>
  );
};

// ─────────────────── Slide 5: Accept & Next Steps ───────────────────
const SlideAccept = ({ readonly, accepted, onAccept, calc }) => (
  <Container>
    <Eyebrow dark={false}>What happens next</Eyebrow>
    <h2 className="text-3xl md:text-5xl font-bold mb-3">Ready to move forward?</h2>
    <p className="text-slate-600 mb-6 max-w-xl">
      If you're happy with the proposed structure, hit <strong>Accept Proposal</strong> below. Your broker will then prepare the formal application, lender comparison, and credit assessment — all aligned to NCCP requirements.
    </p>

    <div className="bg-white rounded-2xl border-2 border-emerald-300 shadow-md p-5 mb-6 proposal-pulse-glow relative overflow-hidden">
      <div className="absolute inset-0 proposal-shimmer pointer-events-none" />
      <p className="text-[11px] uppercase tracking-wide font-bold text-emerald-700 mb-1 relative">YOUR HEADLINE OUTCOME</p>
      <p className="text-3xl font-bold text-slate-900 relative proposal-pulse-number">{formatCurrency(calc.redrawStrategy.totalSavings)} lifetime interest reduction</p>
      <p className="text-sm text-slate-600 mt-1 relative">{calc.redrawStrategy.yearsSaved} years sooner mortgage-free, {formatCurrency(calc.consolidated.monthlySavings)}/mo cash freed up.</p>
    </div>

    {accepted ? (
      <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-300 text-emerald-800 rounded-xl p-4">
        <Award className="h-6 w-6" />
        <div>
          <p className="font-bold">Proposal accepted ✅</p>
          <p className="text-sm">Your broker will be in touch shortly to start the formal application.</p>
        </div>
      </div>
    ) : (
      <Button
        onClick={onAccept}
        disabled={readonly === false ? false : false}
        data-testid="proposal-accept-btn"
        className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-6 text-lg shadow-lg"
      >
        <CheckCircle2 className="h-5 w-5 mr-2" /> Accept Proposal
      </Button>
    )}

    <p className="text-[10px] text-slate-500 mt-6 max-w-2xl leading-relaxed">
      Please do not rely on this report as financial or credit advice, as the figures and information provided are estimates only. This information is general in nature and should be considered in conjunction with the NCCP requirements, your full application details, and the formal loan and credit documentation provided by the lender should you decide to proceed further.
    </p>
  </Container>
);

// ─────────────────── Slide 5: Lender Options ───────────────────
// Shows ALL lender options the broker filled in on the source page (passed
// via payload.brokerContext.lenders). Visible only when at least one lender
// has a name + rate. The recommended lender card is upgraded with an
// emerald glow + Top Pick badge to focus the conversation.
const LenderCard = ({ lender, isRec, monthlyRepayment }) => {
  const features = lender?.features || {};
  const featureList = [];
  if (features.offset) featureList.push('Offset');
  if (features.redraw) featureList.push('Redraw');
  if (features.extraPayments) featureList.push('Extra Payments');
  return (
    <div
      className={`relative rounded-2xl p-5 border-2 transition-all ${
        isRec
          ? 'bg-emerald-500/15 border-emerald-400 shadow-2xl shadow-emerald-500/30 proposal-pulse-glow'
          : 'bg-white/5 border-white/10 hover:border-white/30'
      }`}
    >
      {isRec && (
        <div className="absolute -top-3 left-4 inline-flex items-center gap-1 bg-emerald-500 text-slate-950 text-[10px] uppercase tracking-wide font-bold px-2 py-0.5 rounded shadow-lg">
          <Star className="h-3 w-3 fill-slate-950" /> Top Pick
        </div>
      )}
      <div className="flex items-start justify-between mb-2">
        <p className="text-base font-bold text-white">{lender?.name || 'Lender'}</p>
        {isRec && <ShieldCheck className="h-5 w-5 text-emerald-400" />}
      </div>
      <p className={`text-3xl font-bold mt-1 ${isRec ? 'text-emerald-300 proposal-pulse-number' : 'text-white'}`}>
        {lender?.rate ? Number(lender.rate).toFixed(2) : '—'}<span className="text-base font-normal text-slate-300">%</span>
      </p>
      <p className="text-xs text-slate-300 mt-1">{lender?.type || 'Variable'} · {lender?.repayment || 'Principal & Interest'}</p>
      <div className="mt-3 flex flex-wrap gap-1.5 min-h-[24px]">
        {featureList.length > 0 ? featureList.map((b) => (
          <span key={b} className="text-[10px] bg-white/10 border border-white/15 rounded px-1.5 py-0.5 uppercase tracking-wide font-bold">{b}</span>
        )) : (
          <span className="text-[10px] text-slate-500 italic">No features specified</span>
        )}
      </div>
      <div className="mt-4 pt-3 border-t border-white/10">
        <p className="text-[10px] uppercase tracking-wide font-bold text-slate-400">Estimated monthly</p>
        <p className={`text-lg font-bold ${isRec ? 'text-emerald-300' : 'text-white'}`}>{formatCurrency(monthlyRepayment)}</p>
      </div>
    </div>
  );
};

const SlideLenderOptions = ({ payload, calc }) => {
  const broker = payload?.brokerContext || {};
  const lenders = Array.isArray(broker.lenders) ? broker.lenders.filter((l) => l && (l.name || l.rate)) : [];
  const recommendedLenderIdx = (Number(broker.recommendedLender) || 1) - 1;
  // Use the consolidated mortgage balance for the repayment estimates so the
  // numbers match the strategy slides above.
  const newBalance = calc.consolidated?.newMortgageAmount || 0;
  return (
    <Container dark>
      <Eyebrow>Lender Comparison</Eyebrow>
      <h2 className="text-3xl md:text-5xl font-bold mb-3">Three options. One we recommend.</h2>
      <p className="text-slate-300 mb-6 max-w-2xl text-sm">
        We compared rates, structures and features across {lenders.length || 3} lenders for a {formatCurrency(newBalance)} consolidated home loan. Here's how they stack up — and which one we'd take if it were our own loan.
      </p>

      {lenders.length > 0 ? (
        <div className="grid md:grid-cols-3 gap-4">
          {lenders.map((l, i) => (
            <LenderCard
              key={i}
              lender={l}
              isRec={l.recommended || i === recommendedLenderIdx}
              monthlyRepayment={monthlyAmort(newBalance, Number(l.rate), 30)}
            />
          ))}
        </div>
      ) : (
        <div className="rounded-2xl border-2 border-dashed border-white/15 p-8 text-center text-slate-400">
          <p className="text-sm">No lender options were attached to this proposal.</p>
        </div>
      )}
    </Container>
  );
};


// ─────────────────── Slide registry ───────────────────
// The lender-options slide is included only when the proposal payload has
// at least one lender attached (broker filled it in on the source page).
const hasLenders = (payload) => {
  const arr = payload?.brokerContext?.lenders;
  if (!Array.isArray(arr)) return false;
  return arr.some((l) => l && (l.name || l.rate));
};

const FULL_SLIDES = [
  { key: 'cover', title: 'Cover', component: SlideCover },
  { key: 'current', title: 'Current Situation', component: SlideCurrent },
  { key: 'proposal', title: "What We're Proposing", component: SlideProposal },
  { key: 'consolidation-reduction', title: 'Debt Consolidation Reduction', component: SlideConsolidationReduction },
  { key: 'strategy', title: 'How the Redraw Strategy Works', component: SlideStrategy },
  { key: 'reduction', title: 'Detailed Comparison', component: SlideReduction },
  { key: 'lenders', title: 'Lender Options', component: SlideLenderOptions },
  { key: 'accept', title: 'Accept', component: SlideAccept },
];

const SLIDES_WITHOUT_LENDERS = FULL_SLIDES.filter((s) => s.key !== 'lenders');

// Backward-compat: the default registry is the longest deck. Consumers that
// need the actual slide count for a specific proposal should call
// slidesFor(payload) so the lender slide drops out when not applicable.
export const CONSOLIDATION_SLIDES = FULL_SLIDES;
export const slidesFor = (payload) => (hasLenders(payload) ? FULL_SLIDES : SLIDES_WITHOUT_LENDERS);

/** Renderer — picks the right slide and computes calc once. */
export const renderSlide = ({ index, payload, clientName, brokerName, readonly, accepted, onAccept }) => {
  const list = slidesFor(payload);
  const slide = list[index] || list[0];
  const SlideComp = slide.component;
  const calc = computeConsolidation(payload || {});
  return (
    <SlideComp
      payload={payload}
      calc={calc}
      clientName={clientName}
      brokerName={brokerName}
      readonly={readonly}
      accepted={accepted}
      onAccept={onAccept}
    />
  );
};

export default CONSOLIDATION_SLIDES;
